import React, { useCallback, useEffect, useState } from 'react';

import {
  Box,
  SwipeableDrawer,
  Theme,
  Typography,
  useMediaQuery,
} from '@mui/material';

import { Province } from '../../@types/KingdomResponse';
import { KdKanban, ProvinceLite } from '../../@types/State';
import { useIntelState } from '../../context/IntelState';
import { setKdPlan, useOwnData } from '../../hooks/useOwnData';
import RestUtilities from '../../services/RestUtilities';
import { initialState } from '../WarPlanner/helpers';
import Kanban from '../WarPlanner/Kanban';
import TopActions from '../WarPlanner/TopActions';

const iOS =
  typeof navigator !== 'undefined' &&
  /iPad|iPhone|iPod/.test(navigator.userAgent);

// https://codesandbox.io/s/-w5szl?file=/src/index.js:2304-2334

const KingdomRoles = () => {
  const { intelState, intelDispatch } = useIntelState();
  const [province, setProvince] = useState<ProvinceLite | undefined>(undefined);
  const [message, setMessage] = useState<string>('');
  const [showNotes, setShowNotes] = useState<boolean>(false);
  const matchesSmallWidth = useMediaQuery((theme: Theme) =>
    theme.breakpoints.down('md')
  );
  useOwnData();

  const state: KdKanban = intelState.ownPlan
    ? intelState.ownPlan
    : initialState;
  const provinces = intelState.ownData?.provinces.map((x: Province) => {
    return {
      id: x.id,
      name: x.name,
      race: x.race,
      slot: `[${x.id}]`,
    } as ProvinceLite;
  });

  const loadData = useCallback(async () => {
    if (intelState.ownData) {
      setKdPlan(intelState.ownData?.location, intelState.server, intelDispatch);
      setMessage('reloaded');
    }
  }, [intelDispatch, intelState.ownData, intelState.server]);

  useEffect(() => {
    const interval = setInterval(() => {
      setMessage('');
    }, 3000);
    return () => {
      clearInterval(interval);
    };
  }, [message]);

  const toggleDrawer = () => {
    setShowNotes(!showNotes);
  };

  const setState = (newState: KdKanban) => {
    intelDispatch({ type: 'SET_OWN_PLAN', payload: newState });
  };

  const handleLoadClick = async (event: any) => {
    await loadData();
  };

  const handleSaveClick = async (event: any) => {
    const response = await RestUtilities.post(
      `WarPlan/v1/Post?server=${intelState.server}&location=${intelState.ownData?.location}`,
      { json: JSON.stringify(state) }
    );
    if (response.ok) {
      setMessage('Saved');
    } else {
      setMessage('Something went wrong');
    }
  };

  return (
    <Box
      className='OPs planner'
      sx={{ display: 'flex', height: '100%', marginTop: 1 }}
    >
      <Box
        sx={{
          py: 2,
          px: 1
        }}>
        <TopActions
          provinces={provinces}
          handleLoadClick={handleLoadClick}
          state={state}
          setState={setState}
          message={message}
          handleSaveClick={handleSaveClick}
          showNotes={showNotes}
          setShowNotes={setShowNotes}
          isWarPlan={false}
        />
        <Box
          sx={{
            display: matchesSmallWidth ? 'block' : 'flex',
            mt: 2
          }}>
          <Kanban state={state} isEnemy={false} />
        </Box>

        <Box
          sx={{
            mt: 2
          }}>
          <Typography variant='body1' component='div' sx={{ marginRight: 2 }}>
            Help: Click All to add all. Click + to add a group per role
            (Attack/Hybrid/PureTm), Then organise your KD.
          </Typography>
          <Typography variant='body1' component='div' sx={{ marginRight: 2 }}>
            Add Nicknames by utilising the notes field inside each province.
          </Typography>
          <Typography variant='body1' component='div' sx={{ marginRight: 2 }}>
            To Delete unused groups, move a province in and move province back.
            Column will delete automatically (will be improved).
          </Typography>
          <Typography variant='body1' component='div' sx={{ marginRight: 2 }}>
            To Add a support request, click/select province and select the OP
            from the toolbar appearing on the left.
          </Typography>
        </Box>

        <SwipeableDrawer
          anchor='right'
          open={showNotes}
          onClose={toggleDrawer}
          onOpen={toggleDrawer}
          disableBackdropTransition={!iOS}
          disableDiscovery={iOS}
        >
          <Box
            sx={{
              p: 2
            }}>
            {state.columns.map((c: any, i: number) => (
              <Typography
                key={`col_${i}`}
                variant='h6'
                component='div'
                gutterBottom
              >
                <span style={{ color: 'orange' }}>{c.title}</span>
                {c.items.map((item: any, ii: number) => (
                  <Typography
                    key={`item_${ii}`}
                    variant='h6'
                    component='div'
                    gutterBottom
                  >
                    {`[${item.province.id}] ${item.province.name}${
                      item.content ? ` (${item.content})` : ''
                    }`}
                  </Typography>
                ))}
              </Typography>
            ))}
          </Box>
        </SwipeableDrawer>
      </Box>
    </Box>
  );
};

export default KingdomRoles;
